import { useRef, useState } from 'react';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Badge, Button, Card, EmptyState, ErrorAlert, Field, PageHeader, Pagination, Spinner } from '../components/ui';
import { ASPECT_RATIOS } from '@kontentowo/config';

export function MediaLibraryPage() {
  const { data: brands } = useAsync(() => api.brands.list(), []);
  const [brandId, setBrandId] = useState('');
  const { data, error, loading, reload } = useAsync(() => api.media(brandId || undefined), [brandId]);
  const [prompt, setPrompt] = useState('');
  const [ratio, setRatio] = useState('1:1');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<unknown>(null);
  const [note, setNote] = useState<string | null>(null);
  const [assetTagDrafts, setAssetTagDrafts] = useState<Record<string, string>>({});
  const [page, setPage] = useState(1);
  const fileRef = useRef<HTMLInputElement>(null);

  const effectiveBrand = brandId || brands?.[0]?.id || '';

  const generate = async () => {
    if (!effectiveBrand || !prompt.trim()) return;
    setBusy(true);
    setActionError(null);
    setNote(null);
    try {
      const asset = await api.mediaGenerate({ brandId: effectiveBrand, prompt, aspectRatio: ratio });
      setNote(`Wygenerowano grafikę (${asset.provider}).`);
      setPrompt('');
      reload();
    } catch (err) {
      setActionError(err);
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file: File) => {
    if (!effectiveBrand) return;
    setBusy(true);
    setActionError(null);
    setNote(null);
    try {
      await api.mediaUpload(effectiveBrand, file);
      setNote('Plik wgrany.');
      if (fileRef.current) fileRef.current.value = '';
      reload();
    } catch (err) {
      setActionError(err);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    if (!confirm('Usunąć plik?')) return;
    try {
      await api.mediaDelete(id);
      reload();
    } catch (err) {
      setActionError(err);
    }
  };

  const saveTags = async (id: string, fallback: string) => {
    try {
      const value = assetTagDrafts[id] ?? fallback;
      await api.mediaUpdateTags(id, value.split(',').map((tag) => tag.trim()).filter(Boolean));
      setNote('Tagi assetu zapisane. BloodCrimes może ich użyć przy dopasowaniu materiału do eventu.');
      reload();
    } catch (err) {
      setActionError(err);
    }
  };

  return (
    <>
      <PageHeader
        title="Media Library"
        subtitle="Grafiki, video i materiały marki. Storage: R2 (produkcja) / symulowany lokalnie."
      />
      <ErrorAlert error={actionError} />
      {note && <div className="alert alert--success">{note}</div>}

      <div className="grid grid--2">
        <Card title="Generuj grafikę">
          <Field label="Marka">
            <select className="select" value={brandId} onChange={(e) => setBrandId(e.target.value)}>
              <option value="">— wybierz markę —</option>
              {brands?.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Prompt">
            <textarea className="textarea" value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="np. neonowy dungeon w stylu sci-fi, cinematic" />
          </Field>
          <Field label="Format">
            <select className="select" value={ratio} onChange={(e) => setRatio(e.target.value)}>
              {ASPECT_RATIOS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </Field>
          <div className="btn-row">
            <Button variant="primary" onClick={generate} disabled={busy || !effectiveBrand || !prompt.trim()}>
              {busy ? 'Generowanie…' : 'Generate image'}
            </Button>
            <label className="btn" style={{ cursor: 'pointer' }}>
              Upload
              <input
                ref={fileRef}
                type="file"
                accept="image/*,video/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) upload(f);
                }}
              />
            </label>
          </div>
          <p className="muted" style={{ marginTop: 10 }}>
            Workers AI (Flux) jest używany, gdy binding jest dostępny; inaczej KIE.ai, a bez kluczy — mock.
          </p>
        </Card>

        <Card title="Filtruj / podsumowanie">
          <Field label="Marka">
            <select className="select" value={brandId} onChange={(e) => setBrandId(e.target.value)}>
              <option value="">— wszystkie marki —</option>
              {brands?.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </Field>
          <p className="muted">Assetów: {data?.length ?? 0}</p>
        </Card>
      </div>

      <div style={{ marginTop: 20 }}>
        {loading && <Spinner />}
        {error ? <ErrorAlert error={error} /> : null}
        {data && data.length === 0 && <EmptyState icon="▣" title="Brak mediów" hint="Wygeneruj grafikę lub wgraj plik." />}
        {data && data.length > 0 && (
          <>
            <div className="grid grid--3">
              {data.slice((page - 1) * 12, page * 12).map((a) => (
                <div key={a.id} className="card" style={{ overflow: 'hidden' }}>
                <div style={{ background: 'var(--surface-2)', display: 'grid', placeItems: 'center', height: 160, overflow: 'hidden' }}>
                  {a.mimeType.startsWith('image/') ? (
                    <img src={a.url ?? ''} alt={a.id} style={{ maxWidth: '100%', maxHeight: 160, objectFit: 'cover' }} />
                  ) : a.mimeType.startsWith('video/') ? (
                    <video
                      src={a.url ?? undefined}
                      controls
                      preload="metadata"
                      playsInline
                      style={{ width: '100%', height: 160, objectFit: 'contain', background: '#000' }}
                    />
                  ) : (
                    <span className="muted">{a.mimeType}</span>
                  )}
                </div>
                <div style={{ padding: 12 }}>
                  <div className="inline" style={{ justifyContent: 'space-between' }}>
                    <Badge tone="neutral">{a.type}</Badge>
                    <span className="muted" style={{ fontSize: 11 }}>
                      {a.sizeBytes ? `${Math.round(a.sizeBytes / 1024)} KB` : ''}
                    </span>
                  </div>
                <div className="mono" style={{ fontSize: 11, marginTop: 6, wordBreak: 'break-all' }}>
                  {a.storageKey}
                </div>
                {brands?.some((brand) => brand.id === a.brandId && brand.slug === 'bloodcrimes') && <div style={{ marginTop: 10 }}>
                  <label className="field"><span className="field__label">Asset tags</span><input className="input" value={assetTagDrafts[a.id] ?? (Array.isArray(a.metadata?.tags) ? a.metadata.tags.filter((tag): tag is string => typeof tag === 'string').join(', ') : '')} onChange={(event) => setAssetTagDrafts({ ...assetTagDrafts, [a.id]: event.target.value })} placeholder="gameplay, docks, police, escape" /></label>
                  <Button size="sm" onClick={() => saveTags(a.id, Array.isArray(a.metadata?.tags) ? a.metadata.tags.filter((tag): tag is string => typeof tag === 'string').join(', ') : '')}>Save tags</Button>
                </div>}
                <div className="btn-row" style={{ marginTop: 10 }}>
                    <a className="btn btn--sm" href={a.url ?? '#'} download>
                      Pobierz
                    </a>
                    <Button size="sm" variant="danger" onClick={() => remove(a.id)}>
                      Usuń
                    </Button>
                  </div>
                </div>
                </div>
              ))}
            </div>
            <Pagination page={page} pageSize={12} total={data.length} onChange={setPage} />
          </>
        )}
      </div>
    </>
  );
}
